/* eslint-disable no-param-reassign */
// param reassignment allowed by RTK
import { createSlice } from '@reduxjs/toolkit';
import {
	safeGetItem,
	safeSetItem,
	safeRemoveItem
} from '../../utilities/safeStorage';

const STORAGE_KEY = 'DDC.inventory-compare.selected';
const UUID_REGEX = /^[a-fA-F0-9]{32}$/;

export const initialState = {
	compareVehicles: [],
	compareBarVisible: false
};

export const compareSlice = createSlice({
	name: 'compareVehicles',
	initialState,
	reducers: {
		addCompareVehicle(state, action) {
			state.compareVehicles = [...state.compareVehicles, action.payload];
		},
		removeCompareVehicle(state, action) {
			state.compareVehicles = state.compareVehicles.filter(
				(vehicleUUID) => vehicleUUID !== action.payload
			);
		},
		setCompareVehicles(state, action) {
			state.compareVehicles = action.payload;
		},
		setCompareBarVisible(state, action) {
			state.compareBarVisible = action.payload;
		}
	}
});

export const {
	addCompareVehicle,
	removeCompareVehicle,
	/**
	 * Replaces the compare list without touching sessionStorage. Dispatch
	 * via `hydrateCompareFromStorage` (read path) or wrap in a new thunk
	 * that also calls the sync helper if you need a write path — otherwise
	 * sessionStorage will silently desync from Redux.
	 */
	setCompareVehicles,
	setCompareBarVisible
} = compareSlice.actions;

// Mirrors the compare list to sessionStorage so the selection persists
// across SRP ↔ compare-page navigation (US1918561).
const syncCompareSessionStorage = (uuids) => {
	if (uuids.length > 0) {
		safeSetItem('sessionStorage', STORAGE_KEY, uuids.join(','));
	} else {
		safeRemoveItem('sessionStorage', STORAGE_KEY);
	}
};

// Reads the persisted compare selection from sessionStorage and replaces
// Redux. One-way: does not write back (the value came from there).
export const hydrateCompareFromStorage = () => (dispatch) => {
	const stored = safeGetItem('sessionStorage', STORAGE_KEY);
	if (!stored) return;
	const uuids = [
		...new Set(stored.split(',').filter((uuid) => UUID_REGEX.test(uuid)))
	];
	if (uuids.length > 0) {
		dispatch(setCompareVehicles(uuids));
	}
};

// Selects a vehicle for compare and persists the updated list.
export const selectCompareVehicle = (uuid) => (dispatch, getState) => {
	dispatch(addCompareVehicle(uuid));
	syncCompareSessionStorage(getState().compareVehicles.compareVehicles);
};

// Deselects a vehicle from compare and persists the updated list.
export const deselectCompareVehicle = (uuid) => (dispatch, getState) => {
	dispatch(removeCompareVehicle(uuid));
	syncCompareSessionStorage(getState().compareVehicles.compareVehicles);
};

export default compareSlice.reducer;
